import { createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit"
import dayjs from "dayjs"
import { createSelector } from "reselect"

import {
  Account,
  AnyMessage,
  AssignedConversation,
  CallStateChangedMessage,
  Conversation,
  ConversationStateChangedMessage,
  CustomerMetadata,
  JournalNote,
  MetaData,
  MetadataMessage,
  PatientComment,
  Profile,
  SnoozeMessage,
  User
} from "@doktor-se/bones-ui/dist/web-shared/types"

import { RootState } from "reducers/init"
import { profileRemoved, profileUpdated } from "reducers/users/users.reducer"

import { featureFlags } from "../../config"
import {
  addConversations,
  removeMessageInConversation,
  setComment,
  setJournalNote as setJounalNoteHelper,
  setJournalNoteWebdoc as setJournalWebdocHelper,
  setSnoozeConversation,
  transformConversation,
  unpostponeConversation
} from "./utils/conversations"
import {
  filterActiveAndPostponedConversations,
  filterActiveConversations,
  filterSnoozedConversations,
  filterUpcomingBookings
} from "./utils/filter"
import { setCall, transformMessage } from "./utils/messages"

export interface DiagnosisCode {
  value: string
  label: string
  isFavorite?: boolean
}

// This is a helper object that is used in CarealotV2 in Booked Appointments view
// It contains data of both booking and conversation
export interface BookedAppointment extends Conversation {
  bookingFilter: string
  bookingId: string
  bookingStart: string
  bookingNote: string
  profile?: Profile
  account: Omit<Account, "profiles">
  reassignTo?: string
  lastAssignedStaffId?: string
}

export interface ReassignRequestConversation extends AssignedConversation {
  reassignTo: string
  bookingStart?: string
}

const conversationsAdapter = createEntityAdapter<AssignedConversation>()
const bookedAppointmentsAdapter = createEntityAdapter<BookedAppointment>()
const reassignRequestsAdapter = createEntityAdapter<ReassignRequestConversation>()
const bookedAppointmentsRequestsAdapter = createEntityAdapter<BookedAppointment>()

export interface ConversationsState {
  conversations: EntityState<AssignedConversation, string>
  bookedAppointments: EntityState<BookedAppointment, string>
  reassignRequests: EntityState<ReassignRequestConversation, string>
  bookedAppointmentsRequests: EntityState<BookedAppointment, string>
  webdocDiagnosisCodes?: DiagnosisCode[]
  searchFilter: string
}

const initialState: ConversationsState = {
  conversations: conversationsAdapter.getInitialState(),
  bookedAppointments: bookedAppointmentsAdapter.getInitialState(),
  searchFilter: "",
  reassignRequests: reassignRequestsAdapter.getInitialState(),
  bookedAppointmentsRequests: bookedAppointmentsRequestsAdapter.getInitialState(),
  webdocDiagnosisCodes: undefined
}

const getAllConversationsByPatientID = (state: EntityState<AssignedConversation, string>, patientId: string) =>
  simpleConversationSelectors
    .selectAll(state)
    .filter(conversation => conversation.account && conversation.account.id === patientId)

const conversations = createSlice({
  name: "conversations",
  initialState,
  reducers: {
    loadConversations(state, action: PayloadAction<AssignedConversation[]>) {
      conversationsAdapter.setAll(state.conversations, addConversations(action.payload))
    },
    loadConversation(state, action: PayloadAction<{ conversation: AssignedConversation; user: User }>) {
      // fix because omitted assignedStaffId is not triggering a state update when using update/upsert
      if (
        featureFlags.has("conversations_load_split") &&
        state.conversations.ids.includes(action.payload.conversation.id)
      ) {
        const existingConversation = simpleConversationSelectors.selectById(
          state.conversations,
          action.payload.conversation.id
        )
        const transformedConversation = transformConversation(action.payload.conversation)
        const update = {
          id: action.payload.conversation.id,
          changes: {
            ...transformedConversation,
            // Preserve important fields from existing conversation that might not be in the API response
            lastSeenMessageId: Math.max(
              transformedConversation.lastSeenMessageId ?? 0,
              existingConversation?.lastSeenMessageId ?? 0
            ),
            lastSeenMessageByPatient: Math.max(
              transformedConversation.lastSeenMessageByPatient ?? 0,
              existingConversation?.lastSeenMessageByPatient ?? 0
            ),
            account: transformedConversation.account || existingConversation?.account
          }
        }
        conversationsAdapter.updateOne(state.conversations, update)
      } else {
        conversationsAdapter.removeOne(state.conversations, action.payload.conversation.id)
        conversationsAdapter.addOne(state.conversations, transformConversation(action.payload.conversation))
      }
    },
    removeConversation(state, action: PayloadAction<string>) {
      conversationsAdapter.removeOne(state.conversations, action.payload)
    },
    addMessage(state, action: PayloadAction<AnyMessage>) {
      if (!action.payload.conversationId) return
      const conversation = simpleConversationSelectors.selectById(state.conversations, action.payload.conversationId)
      const changes = conversation && transformMessage(conversation, action.payload)

      changes &&
        conversationsAdapter.updateOne(state.conversations, {
          id: action.payload.conversationId as string,
          changes
        })
    },
    removeMessage(state, action: PayloadAction<{ conversation: AssignedConversation; messageId: number }>) {
      const changes = removeMessageInConversation(action.payload.conversation, action.payload.messageId)

      changes &&
        conversationsAdapter.updateOne(state.conversations, {
          id: action.payload.conversation.id as string,
          changes
        })
    },
    messageRead(state, action: PayloadAction<{ conversationId: string; messageId: number }>) {
      conversationsAdapter.updateOne(state.conversations, {
        id: action.payload.conversationId,
        changes: { lastSeenMessageId: action.payload.messageId }
      })
    },
    messageReadByPatient(state, action: PayloadAction<{ conversationId: string; messageId: number }>) {
      conversationsAdapter.updateOne(state.conversations, {
        id: action.payload.conversationId,
        changes: { lastSeenMessageByPatient: action.payload.messageId }
      })
    },
    patientComment(state, action: PayloadAction<PatientComment>) {
      const conversations = getAllConversationsByPatientID(state.conversations, action.payload.patientId)

      if (!conversations.length) return

      conversations.forEach(conversation =>
        conversationsAdapter.updateOne(state.conversations, {
          id: conversation.id,
          changes: setComment(conversation, action.payload.comment)
        })
      )
    },
    updateConversationState(state, action: PayloadAction<ConversationStateChangedMessage>) {
      if (action.payload.conversationId) {
        conversationsAdapter.updateOne(state.conversations, {
          id: action.payload.conversationId,
          changes: { state: action.payload.data.state }
        })

        if (action.payload.data.state === "closed") {
          bookedAppointmentsAdapter.removeOne(state.bookedAppointments, action.payload.conversationId)
        } else {
          bookedAppointmentsAdapter.updateOne(state.bookedAppointments, {
            id: action.payload.conversationId,
            changes: { state: action.payload.data.state }
          })
        }
      }
    },
    updateCallState(state, action: PayloadAction<CallStateChangedMessage>) {
      if (!action.payload.conversationId) return
      const conversation = simpleConversationSelectors.selectById(state.conversations, action.payload.conversationId)
      const changes = conversation && setCall(conversation, action.payload)
      changes &&
        conversationsAdapter.updateOne(state.conversations, {
          id: action.payload.conversationId as string,
          changes
        })
    },
    snooze(state, action: PayloadAction<SnoozeMessage>) {
      if (!action.payload.conversationId) return

      const conversation = simpleConversationSelectors.selectById(state.conversations, action.payload.conversationId)
      const changes = conversation && setSnoozeConversation(conversation, action.payload)

      changes &&
        conversationsAdapter.updateOne(state.conversations, {
          id: action.payload.conversationId as string,
          changes
        })
    },
    removeSnooze(state, action: PayloadAction<string>) {
      conversationsAdapter.updateOne(state.conversations, {
        id: action.payload as string,
        changes: { snoozedUntil: undefined }
      })
    },
    setSnoozeTimeout(
      state,
      action: PayloadAction<{
        timeout: ReturnType<typeof setTimeout>
        data: { conversationId: string; snoozedUntil?: string }
      }>
    ) {
      conversationsAdapter.updateOne(state.conversations, {
        id: action.payload.data.conversationId,
        changes: { snoozedUntil: action.payload.data.snoozedUntil }
      })
    },
    removePostpone(state, action: PayloadAction<string>) {
      const conversation = simpleConversationSelectors.selectById(state.conversations, action.payload)
      if (!conversation) return
      conversationsAdapter.updateOne(state.conversations, {
        id: action.payload,
        changes: unpostponeConversation(conversation)
      })
    },
    metadata(state, action: PayloadAction<MetadataMessage>) {
      if (!action.payload.conversationId) return
      const conversation = simpleConversationSelectors.selectById(state.conversations, action.payload.conversationId)
      conversation &&
        conversationsAdapter.updateOne(state.conversations, {
          id: action.payload.conversationId,
          changes: { metadata: { ...conversation.metadata, ...action.payload } }
        })
    },
    setJournalNote(state, action: PayloadAction<JournalNote>) {
      if (!action.payload.conversationId) return
      const conversation = simpleConversationSelectors.selectById(state.conversations, action.payload.conversationId)
      conversation &&
        conversationsAdapter.updateOne(state.conversations, {
          id: conversation.id,
          changes: setJounalNoteHelper(conversation, action.payload)
        })
    },
    setJournalNoteWebdoc(state, action: PayloadAction<JournalNote>) {
      if (!action.payload.conversationId) return
      const conversation = simpleConversationSelectors.selectById(state.conversations, action.payload.conversationId)
      conversation &&
        conversationsAdapter.updateOne(state.conversations, {
          id: conversation.id,
          changes: setJournalWebdocHelper(conversation, action.payload)
        })
    },
    updateConversationMetadata(state, action: PayloadAction<{ conversationId: string; metadata: MetaData }>) {
      const conversation = simpleConversationSelectors.selectById(state.conversations, action.payload.conversationId)
      conversation &&
        conversationsAdapter.updateOne(state.conversations, {
          id: action.payload.conversationId,
          changes: { metadata: { ...conversation.metadata, ...action.payload.metadata } }
        })
    },
    updateConversationCustomerMetadata(
      state,
      action: PayloadAction<{ conversationId: string; customerMetadata: CustomerMetadata }>
    ) {
      const conversation = simpleConversationSelectors.selectById(state.conversations, action.payload.conversationId)
      conversation &&
        conversationsAdapter.updateOne(state.conversations, {
          id: action.payload.conversationId,
          changes: { customerMetadata: action.payload.customerMetadata }
        })
    },
    changeProfile(state, action: PayloadAction<{ conversationId: string; profile?: Profile }>) {
      const conversation = simpleConversationSelectors.selectById(state.conversations, action.payload.conversationId)

      if (!conversation) return

      const changes = {
        profile: action.payload.profile,
        history: conversation.history?.map(history => {
          if (history.id === conversation.id) {
            return { ...history, profileId: action.payload.profile?.id }
          }
          return history
        })
      }

      conversationsAdapter.updateOne(state.conversations, {
        id: action.payload.conversationId,
        changes
      })
    },
    setWebdocDiagnosisCodes(state, action: PayloadAction<DiagnosisCode[]>) {
      state.webdocDiagnosisCodes = action.payload
    },
    loadBookedAppointments(state, action: PayloadAction<BookedAppointment[]>) {
      bookedAppointmentsAdapter.setAll(state.bookedAppointments, action.payload)
    },
    setSearchFilter(state, action: PayloadAction<string>) {
      state.searchFilter = action.payload
    },
    loadReassignRequests(state, action: PayloadAction<ReassignRequestConversation[]>) {
      reassignRequestsAdapter.setAll(state.reassignRequests, action.payload)
    },
    updateBookedAppointment(
      state,
      action: PayloadAction<{
        conversationId?: string
        changes: { bookingStart?: string; assignedStaffId?: string | null; bookingNote?: string }
      }>
    ) {
      if (action.payload.conversationId) {
        bookedAppointmentsAdapter.updateOne(state.bookedAppointments, {
          id: action.payload.conversationId,
          changes: action.payload.changes
        })
      }
    },
    removeBookedAppointment(state, action: PayloadAction<string>) {
      bookedAppointmentsAdapter.removeOne(state.bookedAppointments, action.payload)
    },
    addConversationToRequests(state, action: PayloadAction<ReassignRequestConversation>) {
      const conv = action.payload
      const stateConv = state.conversations.entities[conv.id]
      if (stateConv) {
        conversationsAdapter.removeOne(state.conversations, stateConv.id)
      }
      reassignRequestsAdapter.addOne(state.reassignRequests, conv)
    },
    removeFromReassignRequests(state, action: PayloadAction<string>) {
      reassignRequestsAdapter.removeOne(state.reassignRequests, action.payload)
    },
    clearReassignment(state, action: PayloadAction<string>) {
      const id = action.payload
      const conversation = simpleConversationSelectors.selectById(state.conversations, id)
      conversation &&
        conversationsAdapter.updateOne(state.conversations, {
          id,
          changes: { isReassignmentRejected: false }
        })
    },
    loadBookedAppointmentRequests(state, action: PayloadAction<BookedAppointment[]>) {
      bookedAppointmentsRequestsAdapter.setAll(state.bookedAppointmentsRequests, action.payload)
    },
    addConversationToBookedAppointmentRequests(state, action: PayloadAction<string>) {
      const stateConv = state.bookedAppointments.entities[action.payload]
      if (stateConv) {
        bookedAppointmentsAdapter.removeOne(state.bookedAppointments, stateConv.id)
        bookedAppointmentsRequestsAdapter.addOne(state.bookedAppointmentsRequests, stateConv)
      }
    },
    removeConversationFromBookedAppointmentRequests(
      state,
      action: PayloadAction<{ conversationId: string; assignedStaffId?: string | null }>
    ) {
      const stateConv = state.bookedAppointmentsRequests.entities[action.payload.conversationId]
      if (stateConv) {
        bookedAppointmentsRequestsAdapter.removeOne(state.bookedAppointmentsRequests, stateConv.id)
        bookedAppointmentsAdapter.addOne(state.bookedAppointments, stateConv)
        bookedAppointmentsAdapter.updateOne(state.bookedAppointments, {
          id: action.payload.conversationId,
          changes: {
            assignedStaffId: action.payload.assignedStaffId
          }
        })
      }
    }
  },

  extraReducers: builder =>
    builder
      .addCase(profileRemoved, (state, action) => {
        const conversations = getAllConversationsByPatientID(state.conversations, action.payload.patientId)

        if (!conversations.length) return

        conversations.forEach(conversation => {
          if (conversation.profile?.id === action.payload.profileId)
            conversationsAdapter.updateOne(state.conversations, {
              id: conversation.id,
              changes: { profile: undefined }
            })
        })
      })
      .addCase(profileUpdated, (state, action) => {
        const conversations = getAllConversationsByPatientID(state.conversations, action.payload.patientId)

        if (!conversations.length) return

        conversations.forEach(conversation => {
          if (conversation.profile?.id === action.payload.profile.id) {
            conversationsAdapter.updateOne(state.conversations, {
              id: conversation.id,
              changes: { profile: action.payload.profile }
            })
          }
        })
      })
})

export const {
  loadConversations,
  loadConversation,
  removeConversation,
  addMessage,
  removeMessage,
  messageRead,
  messageReadByPatient,
  patientComment,
  updateConversationState,
  updateCallState,
  snooze,
  removeSnooze,
  setSnoozeTimeout,
  removePostpone,
  metadata,
  setJournalNote,
  setJournalNoteWebdoc,
  updateConversationMetadata,
  updateConversationCustomerMetadata,
  changeProfile,
  setWebdocDiagnosisCodes,
  loadBookedAppointments,
  setSearchFilter,
  loadReassignRequests,
  addConversationToRequests,
  removeFromReassignRequests,
  clearReassignment,
  updateBookedAppointment,
  removeBookedAppointment,
  loadBookedAppointmentRequests,
  addConversationToBookedAppointmentRequests,
  removeConversationFromBookedAppointmentRequests
} = conversations.actions

export type ConversationsSliceAction = ObjectFunctionReturnTypes<typeof conversations.actions>

export const simpleConversationSelectors = conversationsAdapter.getSelectors()

export const conversationsSelectors = conversationsAdapter.getSelectors<RootState>(
  state => state.conversations.conversations
)

export const selectConversation = createSelector(
  (state: RootState) => state.conversations.conversations,
  (state: RootState) => state.selected.conversationId,
  (state: RootState) => state.conversations.searchFilter,
  (conversations, conversationId, searchFilter) => {
    if (!conversationId) return undefined

    const conversation = simpleConversationSelectors.selectAll(conversations).find(s => s.id === conversationId)

    if (!conversation) return undefined

    // If there's a search filter and the conversation doesn't match it, return undefined
    if (searchFilter && (!conversation.account?.pnr || !conversation.account.pnr.includes(searchFilter))) {
      return undefined
    }

    return conversation
  }
)

export const selectConversations = createSelector(
  (state: RootState) => state.conversations.conversations,
  (state: RootState) => state.auth.user,
  (state: RootState) => state.conversations.searchFilter,
  (conversations, user, searchFilter) => {
    const open = simpleConversationSelectors
      .selectAll(conversations)
      .filter(c => c.state === "opened")
      .filter(c => !searchFilter || (c.account?.pnr && c.account.pnr.includes(searchFilter)))

    const active = filterActiveConversations(open, user)
    const assignedAndPostponed = filterActiveAndPostponedConversations(open, user)
    const snoozed = filterSnoozedConversations(open, user)
    const upcomingBookingsFromOpened = filterUpcomingBookings(open)
    let waiting: AssignedConversation[] = [...snoozed, ...assignedAndPostponed, ...upcomingBookingsFromOpened]

    const booked = simpleConversationSelectors
      .selectAll(conversations)
      .filter(c => c.state === "booked")
      .filter(c => !searchFilter || (c.account?.pnr && c.account.pnr.includes(searchFilter)))

    const upcomingBookingsFromBooked = filterUpcomingBookings(booked)
    waiting = [...waiting, ...upcomingBookingsFromBooked]

    return {
      active,
      snoozed,
      waiting
    }
  }
)

export const selectAllClinicConversations = createSelector(
  (state: RootState) => state.conversations.conversations,
  conversations => simpleConversationSelectors.selectAll(conversations)
)

export const selectClinicConversations = createSelector(
  (state: RootState) => state.conversations.conversations,
  (state: RootState) => state.auth.user,
  (state: RootState) => state.conversations.searchFilter,
  (state: RootState) => state.booking.personalBookings,
  (conversations, user, searchFilter, bookings) => {
    const open = simpleConversationSelectors
      .selectAll(conversations)
      .filter(c => c.state === "opened" && c.metadata?.bookingId === undefined)
      .filter(c => !searchFilter || (c.account?.pnr && c.account.pnr.includes(searchFilter)))

    const activeConvs = filterActiveConversations(open, user)
    const snoozedConvs = filterSnoozedConversations(open, user)
    const active = [...activeConvs, ...snoozedConvs].sort(
      (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
    )

    const endOfToday = dayjs().endOf("day")

    const booked = simpleConversationSelectors
      .selectAll(conversations)
      .filter(c => c.metadata?.bookingId && bookings.entities[c.metadata?.bookingId])
      .filter(c => c.state === "booked" || c.state === "opened")
      .filter(c => {
        const start = dayjs(bookings.entities[c.metadata!.bookingId!].start)
        return start.isBefore(endOfToday)
      })
      .filter(c => !searchFilter || (c.account?.pnr && c.account.pnr.includes(searchFilter)))

    return {
      active,
      booked
    }
  }
)

export const showMyErrandsNotification = createSelector(
  (state: RootState) => state.conversations.conversations,
  (state: RootState) => state.auth.user,
  (state: RootState) => state.conversations.reassignRequests,
  (state: RootState) => state.conversations.searchFilter,
  (conversations, user, reassignRequests, searchFilter) => {
    // check if there is any activity in assigned conversations
    const open = simpleConversationSelectors
      .selectAll(conversations)
      .filter(c => c.state === "opened")
      .filter(c => !searchFilter || (c.account?.pnr && c.account.pnr.includes(searchFilter)))

    const active = filterActiveConversations(open, user)

    // check if there are any requests waiting for you
    const requestsForYou = reassignRequestsAdapter
      .getSelectors()
      .selectAll(reassignRequests)
      .filter(c => c.reassignTo === user?.id)
    return (
      active.some(
        c => (c.lastMessage?.id && c.lastMessage.id > (c.lastSeenMessageId ?? 0)) || c.callState === "incoming"
      ) || requestsForYou.length > 0
    )
  }
)

export const showBookedAppointmentsNotification = createSelector(
  (state: RootState) => state.conversations.bookedAppointments,
  (state: RootState) => state.conversations.searchFilter,
  (bookedAppointments, searchFilter) => {
    const booked = simpleBookedAppointmentsSelectors
      .selectAll(bookedAppointments)
      .filter(c => c.bookingId && c.bookingStart && c.bookingFilter)
      .filter(c => !searchFilter || (c.account?.pnr && c.account.pnr.includes(searchFilter)))

    const endOfToday = dayjs().endOf("day")

    return booked.filter(c => !c.assignedStaffId && dayjs(c.bookingStart).isBefore(endOfToday)).length > 0
  }
)

export const selectSnoozedConversations = createSelector(
  (state: RootState) => state.conversations.conversations,
  (state: RootState) => state.auth.user,
  (state: RootState) => state.conversations.searchFilter,
  (conversations, user, searchFilter) => {
    const open = simpleConversationSelectors
      .selectAll(conversations)
      .filter(c => c.state === "opened")
      .filter(c => !searchFilter || (c.account?.pnr && c.account.pnr.includes(searchFilter)))
    const snoozed = filterSnoozedConversations(open, user)

    return snoozed
  }
)

const simpleBookedAppointmentsSelectors = bookedAppointmentsAdapter.getSelectors()

export const selectClinicErrands = createSelector(
  (state: RootState) => state.conversations.bookedAppointments,
  (state: RootState) => state.conversations.searchFilter,
  (conversations, searchFilter) => {
    const convs = simpleBookedAppointmentsSelectors
      .selectAll(conversations)
      .filter(c => !searchFilter || (c.account?.pnr && c.account.pnr.includes(searchFilter)))

    const endOfToday = dayjs().endOf("day")
    const bookedInClinic = convs
      .filter(c => c.state === "booked" && c.bookingId && c.bookingFilter && c.bookingStart)
      .sort((a, b) => new Date(a.bookingStart).getTime() - new Date(b.bookingStart).getTime())

    const bookedAppointments = bookedInClinic.filter(
      c => !c.assignedStaffId || dayjs(c.bookingStart).isAfter(endOfToday)
    )

    const ongoing = convs.filter(c => c.state !== "booked")

    const bookedToday = bookedInClinic
      .filter(c => !!c.assignedStaffId)
      .filter(c => dayjs(c.bookingStart).isBefore(endOfToday))

    return {
      bookedAppointments,
      ongoingErrands: ongoing,
      bookedErrands: bookedToday
    }
  }
)

export const reassignRequestsSelectors = reassignRequestsAdapter.getSelectors<RootState>(
  state => state.conversations.reassignRequests
)

export const selectBookedAppointmentsRequests = createSelector(
  (state: RootState) => state.conversations.bookedAppointmentsRequests,
  conversations => {
    const requests = bookedAppointmentsRequestsAdapter.getSelectors().selectAll(conversations)
    const filteredRequests = requests.filter(c => c.bookingStart)
    const endOfToday = dayjs().endOf("day")

    const { ongoing, future }: { ongoing: BookedAppointment[]; future: BookedAppointment[] } = filteredRequests.reduce(
      (acc, item) => {
        const start = dayjs(item.bookingStart)
        const { lastAssignedStaffId } = item
        if (start.isBefore(endOfToday) && !!lastAssignedStaffId) {
          acc.ongoing.push(item)
        } else {
          acc.future.push(item)
        }
        return acc
      },
      { ongoing: [] as BookedAppointment[], future: [] as BookedAppointment[] }
    )

    return {
      ongoingRequests: ongoing,
      futureRequests: future
    }
  }
)

export default conversations.reducer
